import { motion, AnimatePresence } from "motion/react";
import { useState } from "react";

interface Notification {
  id: string;
  type: "test-due" | "reminder" | "quality-alert";
  title: string;
  message: string;
  time: string;
  isRead: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface GlobalNotificationBarProps {
  notifications: Notification[];
  onMarkAsRead: (id: string) => void;
  onDeleteNotification: (id: string) => void;
  onNotificationClick?: (notification: Notification) => void;
}

export default function GlobalNotificationBar({
  notifications,
  onMarkAsRead,
  onDeleteNotification,
  onNotificationClick,
}: GlobalNotificationBarProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const unreadNotifications = notifications.filter((n) => !n.isRead);
  const unreadCount = unreadNotifications.length;

  const notificationColors = {
    "test-due": {
      bg: "#FEF3C7",
      text: "#92400E",
      border: "#F59E0B",
      icon: "#F59E0B",
    },
    reminder: {
      bg: "#D1FAE5",
      text: "#065F46",
      border: "#10B981",
      icon: "#10B981",
    },
    "quality-alert": { bg: "#DBEAFE", text: "#1E40AF", border: "#3B82F6", icon: "#3B82F6" },
  };

  if (unreadCount === 0) return null;

  const displayNotifications = showAll
    ? unreadNotifications
    : unreadNotifications.slice(0, 1);

  return (
    <div className="fixed top-0 left-0 right-0 z-[60] pointer-events-none">
      <div className="max-w-md mx-auto">
        <AnimatePresence>
          <motion.div
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="pointer-events-auto"
          >
            {/* Notification Bar */}
            <div className="px-4 pt-4">
              <div
                className="rounded-3xl overflow-hidden"
                style={{
                  boxShadow:
                    "0 10px 40px rgba(0, 0, 0, 0.15), 0 0 100px rgba(27, 42, 91, 0.08)",
                }}
              >
                {displayNotifications.map((notification, index) => {
                  const colors = notificationColors[notification.type];
                  return (
                    <motion.div
                      key={notification.id}
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: index * 0.1 }}
                      className="backdrop-blur-xl"
                      style={{
                        background: `linear-gradient(135deg, ${colors.bg}f0 0%, ${colors.bg}e0 100%)`,
                        borderBottom:
                          index < displayNotifications.length - 1
                            ? `1px solid ${colors.border}30`
                            : "none",
                      }}
                    >
                      {/* Notification card content removed */}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
